import { useLazyGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberPdfQuery } from "randmar-api-client";
import { Button, useToast } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Download, Printer } from "lucide-react";
import React from "react";
import InlineSpinner from "./InlineSpinner";

interface IProps {
  documentNumber: string;
  disabled?: boolean;
  stealth?: boolean;
}

const OpenPDFButton: React.FC<IProps> = ({
  documentNumber,
  disabled,
  stealth,
}) => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const [trigger, { isFetching }] =
    useLazyGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberPdfQuery();

  const [activeAction, setActiveAction] = React.useState<
    "print" | "download" | null
  >(null);

  React.useEffect(() => {
    if (!isFetching) {
      setActiveAction(null);
    }
  }, [isFetching]);

  const handlePrint = async () => {
    setActiveAction("print");
    try {
      const response = await trigger({
        routeManufacturerId: applicationId,
        documentNumber,
      }).unwrap();

      const file =
        response instanceof Blob
          ? response
          : new Blob([response as BlobPart], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
      window.setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
    } catch (error) {
      console.error("Failed to print PDF", error);
      toast({
        variant: "destructive",
        title: "Error printing document",
        description:
          "There was a problem generating the PDF for printing. Please try again.",
      });
    }
  };

  const handleDownload = async () => {
    setActiveAction("download");
    try {
      const response = await trigger({
        routeManufacturerId: applicationId,
        documentNumber,
      }).unwrap();

      const file =
        response instanceof Blob
          ? response
          : new Blob([response as BlobPart], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.target = "_blank";
      fileLink.download = `${documentNumber}.pdf`;
      fileLink.click();
      window.setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
    } catch (error) {
      console.error("Failed to download PDF", error);
      toast({
        variant: "destructive",
        title: "Error downloading document",
        description:
          "There was a problem generating the PDF for download. Please try again.",
      });
    }
  };

  if (stealth) {
    return (
      <Button
        variant="outline"
        size="icon"
        className="min-h-[44px] min-w-[44px] rounded-xl"
        title={"Open " + documentNumber + " PDF"}
        aria-label={"Open " + documentNumber + " PDF"}
        disabled={disabled || isFetching}
        onClick={handlePrint}
      >
        {isFetching && activeAction === "print" ? (
          <InlineSpinner size="sm" decorative />
        ) : (
          <Printer className="h-4 w-4" />
        )}
      </Button>
    );
  }

  return (
    <>
      <Button
        title={"Download " + documentNumber}
        variant="outline"
        disabled={disabled || isFetching}
        onClick={handleDownload}
        className="mr-2 min-h-[44px] min-w-[44px] rounded-xl"
        size="icon"
      >
        {isFetching && activeAction === "download" ? (
          <InlineSpinner size="sm" decorative />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>

      <Button
        title={"Open " + documentNumber + " in new window"}
        variant="outline"
        size="sm"
        className="min-h-[44px] rounded-xl"
        disabled={disabled || isFetching}
        onClick={handlePrint}
      >
        {isFetching && activeAction === "print" ? (
          <InlineSpinner className="mr-2" decorative />
        ) : (
          <Printer className="mr-2 h-4 w-4" />
        )}
        Print Document
      </Button>
    </>
  );
};

export default OpenPDFButton;
